import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '$partials/ui/button';
import { IconArrowRight, IconChevronDown, IconClose, IconEchelon, IconMenu, NavLogo } from '$partials/icons';
import { resolvePath } from '$utilities/routes';
import { getLocation } from '$utilities/location';
import { useLocale } from '$contexts/locale.jsx';
import LocaleSelector from './locale-selector';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/** @param {Record<string, any> | null | undefined} item */
const getItemHref = (item) => {
    if (!item) { return '/'; }
    if (item.cfMetadata) { return resolvePath(/** @type {any} */ ({ slug: item.slug, cfMetadata: item.cfMetadata })); }
    if (item.slug) { return `/${item.slug}`; }
    return '/';
};

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const Navigation = (/** @type {Props} */ { navigation }) => {
    const { current: locale, options: localeOptions, formatURI } = useLocale();
    const location = getLocation();
    const [isAtTop, setIsAtTop] = useState(true);
    const [isVisible, setIsVisible] = useState(true);
    const [activeDropdown, setActiveDropdown] = useState(/** @type {number | null} */ (null));
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [mobileExpandedIndex, setMobileExpandedIndex] = useState(/** @type {number | null} */ (null));
    const [isMobileLocaleOpen, setIsMobileLocaleOpen] = useState(false);
    const lastScrollY = useRef(0);

    useEffect(() => {
        const handleScroll = () => {
            const currentY = window.scrollY;
            const atTop = currentY < 10;

            setIsAtTop(atTop);

            if (atTop || currentY < lastScrollY.current) {
                setIsVisible(true);
            } else {
                setIsVisible(false);
                setActiveDropdown(null);
            }

            lastScrollY.current = currentY;
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isMobileMenuOpen]);

    if (!navigation) { return null; }

    const nav = /** @type {Record<string, any>} */ (navigation);

    const links = [1, 2, 3, 4, 5]
        .map(i => {
            const title = nav[`link${i}Title`];
            if (!title) { return null; }
            const link = nav[`link${i}`];
            const dropdownItems = nav[`link${i}DropdownCollection`]?.items?.filter(Boolean) ?? [];
            return { title, link, dropdownItems };
        })
        .filter(/** @param {any} item @returns {item is { title: string, link: any, dropdownItems: any[] }} */ item => item !== null);

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
        setMobileExpandedIndex(null);
        setIsMobileLocaleOpen(false);
    };

    return (
        <>
            <header
                className={[
                    'fixed top-0 left-0 right-0 z-50',
                    'transition-[background-color,transform] duration-300 ease-in-out',
                    isVisible ? 'translate-y-0' : '-translate-y-full',
                    isAtTop ? 'bg-transparent' : 'bg-canvas',
                ].join(' ')}
                id='primary-navigation'>

                <div className='layout-container layout-site-outer-padding'>
                    <nav
                        aria-label='Main'
                        className='flex items-center justify-between h-[56px] md:h-[61px]'>

                        <Button
                            as='a'
                            className='flex-shrink-0 text-text-primary inline-flex gap-7 items-center'
                            href='/'>
                            <IconEchelon/>
                            <NavLogo />
                        </Button>

                        {/* Desktop links */}
                        <ul className='hidden md:flex items-center gap-8'>
                            {links.map(({ title, link, dropdownItems }, i) => (
                                <li
                                    className='relative'
                                    key={i}
                                    onMouseEnter={() => setActiveDropdown(dropdownItems.length ? i : null)}
                                    onMouseLeave={() => setActiveDropdown(null)}>

                                    {dropdownItems.length > 0 ? (
                                        <>
                                            <button
                                                aria-expanded={activeDropdown === i}
                                                className='type-style-body-small flex items-center gap-[4px] h-[48px] px-12 text-text-primary rounded-[4px] hover:bg-[var(--sand-100)] cursor-pointer'
                                                type='button'>
                                                {title}
                                                <IconChevronDown
                                                    className={`w-[10px] ml-6 transition-transform duration-200 ${activeDropdown === i ? 'rotate-180' : ''}`}/>
                                            </button>

                                            {/* Dropdown panel */}
                                            <div
                                                className={[
                                                    'absolute left-0 top-full pt-4',
                                                    'transition-all duration-150',
                                                    activeDropdown === i
                                                        ? 'opacity-100 pointer-events-auto translate-y-0'
                                                        : 'opacity-0 pointer-events-none -translate-y-1',
                                                ].join(' ')}>
                                                <ul className='bg-canvas border border-[var(--sand-100)] rounded shadow-[0px_4px_12px_5px_rgba(0,0,0,0.04)] w-[292px] p-4'>
                                                    {dropdownItems.map((item, j) => (
                                                        <li key={j}>
                                                            <Button
                                                                as='a'
                                                                className='group flex items-center gap-8 w-full min-h-[48px] pl-8 pr-12 py-8 rounded-[4px] hover:bg-[var(--sand-100)] text-left'
                                                                href={getItemHref(item)}>
                                                                <span className='flex flex-col flex-1 min-w-0'>
                                                                    <span className='type-style-body-small text-text-primary leading-[1.5]'>
                                                                        {item?.pageTitle}
                                                                    </span>
                                                                    {item.additionalNavigationText && (
                                                                        <span className='text-[12px] text-text-tertiary leading-[1.5]'>
                                                                            {item.additionalNavigationText}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                                <IconArrowRight
                                                                    className='opacity-0 group-hover:opacity-100 transition-opacity duration-150 shrink-0 text-text-primary h-10' />
                                                            </Button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </>
                                    ) : (
                                        <Button
                                            as='a'
                                            className='type-style-body-small h-[48px] px-12 flex items-center text-text-primary rounded-[4px] hover:bg-[var(--sand-100)]'
                                            href={getItemHref(link)}>
                                            {title}
                                        </Button>
                                    )}
                                </li>
                            ))}
                        </ul>

                        {/* Desktop CTA */}
                        <div className='hidden md:inline-flex'>
                            {localeOptions?.length > 1 && <LocaleSelector />}
                            {navigation.ctaLabel && navigation.ctaLink && (
                                <Button
                                    as='a'
                                    className={`button ${isAtTop ? 'button--header-up' : 'button--header-down'}`}
                                    href={getItemHref(navigation.ctaLink)}>
                                    <span className='button__label--animate'>
                                        <span>
                                            {navigation.ctaLabel}
                                        </span>
                                    </span>
                                </Button>
                            )}
                        </div>

                        {/* Mobile hamburger */}
                        <button
                            aria-expanded={isMobileMenuOpen}
                            aria-label='Open menu'
                            className='md:hidden flex items-center justify-center w-16 h-16 text-text-primary'
                            onClick={() => setIsMobileMenuOpen(true)}
                            type='button'>
                            <IconMenu />
                        </button>
                    </nav>
                </div>
                <div
                    aria-hidden={true}
                    className={`absolute bottom-0 left-0 right-0 h-[1px] bg-[var(--border-default)] transition-opacity duration-300 ${isAtTop ? 'opacity-0' : 'opacity-100'}`} />
            </header>

            {/* Mobile menu panel */}
            <div
                aria-hidden={!isMobileMenuOpen}
                className={[
                    'fixed inset-0 z-50 md:hidden flex',
                    'transition-opacity duration-300',
                    isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
                ].join(' ')}>

                {/* Backdrop */}
                <div
                    aria-hidden='true'
                    className='absolute inset-0 bg-black-opacity-20'
                    onClick={closeMobileMenu} />

                <div
                    aria-label='Main menu'
                    aria-modal='true'
                    className={[
                        'relative flex flex-col bg-canvas w-full max-w-[800px] h-full overflow-y-auto',
                        'transition-transform duration-300',
                        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full',
                    ].join(' ')}
                    role='dialog'>

                    {/* Menu top bar */}
                    <div className='flex items-center justify-between h-[56px] px-layout-outer shrink-0 border-b border-[var(--border-default)]'>
                        <Button
                            as='a'
                            className='flex-shrink-0 text-text-primary inline-flex gap-7 items-center'
                            href='/'>
                            <IconEchelon />
                            <NavLogo />
                        </Button>
                        <button
                            aria-label='Close menu'
                            className='flex items-center justify-center w-10 h-10 text-text-primary'
                            onClick={closeMobileMenu}
                            type='button'>
                            <IconClose />
                        </button>
                    </div>

                    {/* Menu items */}
                    <div className='flex-1 flex flex-col px-layout-outer pt-24'>
                        <ul>
                            {links.map(({ title, link, dropdownItems }, i) => (
                                <li
                                    className='border-t border-[var(--border-default)]'
                                    key={i}>
                                    {dropdownItems.length > 0 ? (
                                        <>
                                            <button
                                                aria-expanded={mobileExpandedIndex === i}
                                                className='flex items-center gap-4 w-full py-[20px] text-left'
                                                onClick={() => setMobileExpandedIndex(mobileExpandedIndex === i ? null : i)}
                                                type='button'>
                                                <span className={`flex-1 type-style-body transition-colors duration-100 ${mobileExpandedIndex === i ? 'text-text-secondary' : 'text-text-primary'}`}>
                                                    {title}
                                                </span>
                                                <span className='flex items-center justify-center w-40 h-40 rounded bg-[var(--sand-100)] shrink-0'>
                                                    <IconChevronDown
                                                        className={`w-[10px] transition-transform duration-100 ${mobileExpandedIndex === i ? 'rotate-180' : ''}`} />
                                                </span>
                                            </button>

                                            <div
                                                className={[
                                                    'overflow-hidden transition-[max-height] duration-100 ease-in-out',
                                                    mobileExpandedIndex === i ? 'max-h-[400px]' : 'max-h-0',
                                                ].join(' ')}>
                                                <ul className='pb-6'>
                                                    {dropdownItems.map((item, j) => (
                                                        <li key={j}>
                                                            <Button
                                                                as='a'
                                                                className='flex items-center w-full py-[10.5px] type-style-body text-text-primary'
                                                                href={getItemHref(item)}
                                                                onClick={closeMobileMenu}>
                                                                {item?.pageTitle}
                                                            </Button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </>
                                    ) : (
                                        <Button
                                            as='a'
                                            className='flex items-center w-full py-[20px] type-style-body text-text-primary'
                                            href={getItemHref(link)}
                                            onClick={closeMobileMenu}>
                                            {title}
                                        </Button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Mobile locale selector */}
                    {localeOptions?.length > 1 && (
                        <div className='px-layout-outer shrink-0'>
                            <div className='border-t border-[var(--border-default)]'>
                                <button
                                    aria-expanded={isMobileLocaleOpen}
                                    className='flex items-center gap-4 w-full py-[20px] text-left'
                                    onClick={() => setIsMobileLocaleOpen(prev => !prev)}
                                    type='button'>
                                    <span className={`flex-1 type-style-body transition-colors duration-100 ${isMobileLocaleOpen ? 'text-text-secondary' : 'text-text-primary'}`}>
                                        {locale?.labelLong}
                                    </span>
                                    <span className='flex items-center justify-center w-40 h-40 rounded bg-[var(--sand-100)] shrink-0'>
                                        <IconChevronDown
                                            className={`w-[10px] transition-transform duration-100 ${isMobileLocaleOpen ? 'rotate-180' : ''}`} />
                                    </span>
                                </button>
                                <div
                                    className={[
                                        'overflow-hidden transition-[max-height] duration-100 ease-in-out',
                                        isMobileLocaleOpen ? 'max-h-[400px]' : 'max-h-0',
                                    ].join(' ')}>
                                    <ul className='pb-6'>
                                        {(/** @type {any[]} */ ([...localeOptions]).sort((a, b) => b.code === (/** @type {any} */ (locale))?.code ? 1 : -1)).map((option, i) => (
                                            <li key={i}>
                                                <a
                                                    className='flex items-center w-full py-[10.5px] type-style-body text-text-primary'
                                                    href={(/** @type {any} */ (formatURI))(location.pathname, option.code)}
                                                    onClick={closeMobileMenu}>
                                                    {option.labelLong}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Mobile CTA */}
                    {navigation.ctaLabel && navigation.ctaLink && (
                        <div className='px-layout-outer py-4 shrink-0'>
                            <Button
                                as='a'
                                className='button button--primary w-full justify-center'
                                href={getItemHref(navigation.ctaLink)}
                                onClick={closeMobileMenu}>
                                {navigation.ctaLabel}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const navLinkShape = PropTypes.shape({
    slug: PropTypes.string,
    title: PropTypes.string,
    pageTitle: PropTypes.string,
    heading: PropTypes.string,
    internalName: PropTypes.string,
    cfMetadata: PropTypes.shape({
        concepts: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
    }),
});

const propTypes = {
    navigation: PropTypes.shape({
        ctaLabel: PropTypes.string,
        ctaLink: navLinkShape,
        link1Title: PropTypes.string,
        link1: navLinkShape,
        link1DropdownCollection: PropTypes.shape({ items: PropTypes.arrayOf(navLinkShape) }),
        link2Title: PropTypes.string,
        link2: navLinkShape,
        link2DropdownCollection: PropTypes.shape({ items: PropTypes.arrayOf(navLinkShape) }),
        link3Title: PropTypes.string,
        link3: navLinkShape,
        link3DropdownCollection: PropTypes.shape({ items: PropTypes.arrayOf(navLinkShape) }),
        link4Title: PropTypes.string,
        link4: navLinkShape,
        link4DropdownCollection: PropTypes.shape({ items: PropTypes.arrayOf(navLinkShape) }),
        link5Title: PropTypes.string,
        link5: navLinkShape,
        link5DropdownCollection: PropTypes.shape({ items: PropTypes.arrayOf(navLinkShape) }),
    }),
};

Navigation.propTypes = propTypes;

export default Navigation;
